import {FC, useMemo} from 'react';
import {Box, Card, CardContent, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {AdminBatchSubmission} from '../../../types/adminBatch.ts';

const UNMATCHED_DISPLAY_LIMIT = 100;

type BackfillEntry = { dictId?: number; word: string; pos?: string; chunkIndex: number };
type BackfillSummary = {
    totalRequested: number; totalResponseItems: number; matchedCount: number;
    unmatchedCount: number; extraResponseCount: number; unmatchedEntries: BackfillEntry[];
};

const parseJsonArray = (value?: string): unknown[] => {
    if (!value) return [];
    try { const p = JSON.parse(value); return Array.isArray(p) ? p : []; } catch { return []; }
};
const parseJsonObject = (value?: string): Record<string, unknown> => {
    if (!value) return {};
    try { const p = JSON.parse(value); return p && typeof p === 'object' && !Array.isArray(p) ? p as Record<string, unknown> : {}; } catch { return {}; }
};
const normWord = (v: unknown) => String(v ?? '').trim().toLowerCase();
const normPos = (v: unknown) => String(v ?? '').trim().toUpperCase();
const consumeCount = (map: Map<string, number>, key: string): boolean => {
    const c = map.get(key) ?? 0;
    if (c <= 0) return false;
    if (c === 1) map.delete(key); else map.set(key, c - 1);
    return true;
};

const buildSummary = (submissions: AdminBatchSubmission[]): BackfillSummary => {
    let totalRequested = 0, totalResponseItems = 0, matchedCount = 0, unmatchedCount = 0, extraResponseCount = 0;
    const unmatchedEntries: BackfillEntry[] = [];

    submissions.forEach((sub) => {
        const reqObj = parseJsonObject(sub.requestJson);
        const resItems = parseJsonArray(sub.responseJson);
        const reqEntries: BackfillEntry[] = (Array.isArray(reqObj.entries) ? reqObj.entries : [])
            .map((raw: unknown) => {
                if (!raw || typeof raw !== 'object') return null;
                const item = raw as Record<string, unknown>;
                const word = String(item.word ?? '').trim();
                if (!word) return null;
                const dictIdRaw = item.dictId;
                const dictId = Number.isFinite(Number(dictIdRaw)) ? Number(dictIdRaw) : undefined;
                const pos = String(item.pos ?? '').trim() || undefined;
                return {dictId, word, pos, chunkIndex: sub.chunkIndex} as BackfillEntry;
            })
            .filter((v: BackfillEntry | null): v is BackfillEntry => !!v);

        totalRequested += reqEntries.length;
        totalResponseItems += resItems.length;

        const resByWordPos = new Map<string, number>();
        const resByWord = new Map<string, number>();
        resItems.forEach((raw) => {
            if (!raw || typeof raw !== 'object') return;
            const item = raw as Record<string, unknown>;
            const term = normWord(item.term);
            if (!term) return;
            const pos = normPos(item.pos);
            resByWordPos.set(`${term}||${pos}`, (resByWordPos.get(`${term}||${pos}`) ?? 0) + 1);
            resByWord.set(term, (resByWord.get(term) ?? 0) + 1);
        });

        reqEntries.forEach((entry) => {
            const word = normWord(entry.word);
            const pos = normPos(entry.pos);
            const matched = pos
                ? consumeCount(resByWordPos, `${word}||${pos}`) && consumeCount(resByWord, word)
                : consumeCount(resByWord, word);
            if (matched) matchedCount++; else { unmatchedCount++; unmatchedEntries.push(entry); }
        });
        resByWord.forEach((count) => { extraResponseCount += count; });
    });

    return {totalRequested, totalResponseItems, matchedCount, unmatchedCount, extraResponseCount, unmatchedEntries};
};

interface BackfillJobDetailProps { submissions: AdminBatchSubmission[]; }

export const BackfillJobDetail: FC<BackfillJobDetailProps> = ({submissions}) => {
    const summary = useMemo(() => buildSummary(submissions), [submissions]);

    return (
        <Card variant="outlined">
            <CardContent sx={{p: 0}}>
                <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider'}}>
                    <Typography variant="subtitle2">사전 백필 결과 요약</Typography>
                    <Typography variant="caption" color="text.secondary">request_json(entries)와 response_json(term,pos) 매칭 기준</Typography>
                    <Stack direction="row" spacing={1} sx={{mt: 1}} flexWrap="wrap" useFlexGap>
                        <Chip size="small" color="success" variant="outlined" label={`매칭 성공 ${summary.matchedCount}`}/>
                        <Chip size="small" color="error" variant="outlined" label={`미반영 ${summary.unmatchedCount}`}/>
                        <Chip size="small" variant="outlined" label={`요청 ${summary.totalRequested}`}/>
                        <Chip size="small" variant="outlined" label={`응답 ${summary.totalResponseItems}`}/>
                        <Chip size="small" color={summary.extraResponseCount > 0 ? 'warning' : 'default'} variant="outlined" label={`응답 초과 ${summary.extraResponseCount}`}/>
                        <Chip size="small" color={summary.totalRequested > 0 && (summary.matchedCount / summary.totalRequested) >= 0.95 ? 'success' : 'warning'} variant="filled"
                            label={`성공률 ${(summary.totalRequested > 0 ? (summary.matchedCount / summary.totalRequested) * 100 : 0).toFixed(1)}%`}/>
                    </Stack>
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>청크</TableCell>
                            <TableCell>dictId</TableCell>
                            <TableCell>단어</TableCell>
                            <TableCell>POS</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {summary.unmatchedEntries.slice(0, UNMATCHED_DISPLAY_LIMIT).map((entry, idx) => (
                            <TableRow key={`${entry.chunkIndex}-${entry.dictId ?? 'x'}-${entry.word}-${entry.pos ?? 'x'}-${idx}`}>
                                <TableCell>{entry.chunkIndex}</TableCell>
                                <TableCell>{entry.dictId ?? '-'}</TableCell>
                                <TableCell>{entry.word}</TableCell>
                                <TableCell>{entry.pos || '-'}</TableCell>
                            </TableRow>
                        ))}
                        {summary.unmatchedEntries.length === 0 && (
                            <TableRow><TableCell colSpan={4} align="center" sx={{py: 2}}><Typography variant="body2" color="text.secondary">미반영 엔트리가 없습니다.</Typography></TableCell></TableRow>
                        )}
                        {summary.unmatchedEntries.length > UNMATCHED_DISPLAY_LIMIT && (
                            <TableRow><TableCell colSpan={4} align="center" sx={{py: 1.5}}><Typography variant="caption" color="text.secondary">미반영 엔트리가 많아 상위 {UNMATCHED_DISPLAY_LIMIT}개만 표시했습니다. (총 {summary.unmatchedEntries.length}개)</Typography></TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};
